import { Trans, t } from "@lingui/macro";
import { useMemo, useState } from "react";
import { Address } from "viem";

import { APP_NAME } from "config/ui";
import { useGmMarketsApy } from "domain/synthetics/markets/useGmMarketsApy";
import { convertPoolsTimeRangeToApyPeriod, usePoolsTimeRange } from "domain/synthetics/markets/usePoolsTimeRange";
import useV2Stats from "domain/synthetics/stats/useV2Stats";
import { useChainId } from "lib/chains";
import { formatUsd } from "lib/numbers";
import useWallet from "lib/wallets/useWallet";

import Button from "components/Button/Button";
import Footer from "components/Footer/Footer";
import PageTitle from "components/PageTitle/PageTitle";
import { GmList } from "components/Synthetics/GmList/GmList";
import { TradeHistory } from "components/Synthetics/TradeHistory/TradeHistory";
import Tabs from "components/Tabs/Tabs";

enum PoolsTab {
  Pools = "Pools",
  History = "History",
}

export default function Pools() {
  const { timeRange, setTimeRange } = usePoolsTimeRange();
  const [activeTab, setActiveTab] = useState<PoolsTab>(PoolsTab.Pools);

  const { chainId } = useChainId();
  const { account } = useWallet();

  const apyPeriod = useMemo(() => convertPoolsTimeRangeToApyPeriod(timeRange), [timeRange]);

  const {
    marketsTokensApyData,
    marketsTokensIncentiveAprData,
    glvTokensIncentiveAprData,
    marketsTokensLidoAprData,
    glvApyInfoData,
  } = useGmMarketsApy(chainId, { period: apyPeriod });

  return (
    <div className="default-container page-layout">
      <PageTitle
        title={t`${APP_NAME} Pool`}
        subtitle={t`Pools providing liquidity to specific Nectarliquid markets, supporting single-asset and native asset options.`}
        hasBackButton
        subContent={<PoolsTvl />}
        showNetworkIcon={false}
      />

      <div className="mb-24 mt-8 flex flex-col gap-16">
        <div className="flex items-center justify-between">
          <Tabs
            options={Object.values(PoolsTab).map((tab) => ({ label: tab, value: tab }))}
            selectedValue={activeTab}
            onChange={setActiveTab}
            type="inline"
          />
        </div>

        {activeTab === PoolsTab.Pools && (
          <div id="pools-list">
            {/* {!isBotanix && (
              <GlvList
                marketsTokensApyData={marketsTokensApyData}
                marketsTokensIncentiveAprData={marketsTokensIncentiveAprData}
                glvTokensIncentiveAprData={glvTokensIncentiveAprData}
                marketsTokensLidoAprData={marketsTokensLidoAprData}
                glvTokensApyData={glvApyInfoData}
                glvPerformance={glvPerformance}
                gmPerformance={gmPerformance}
                glvPerformanceSnapshots={glvPerformanceSnapshots}
                gmPerformanceSnapshots={gmPerformanceSnapshots}
                isDeposit
              />
            )} */}
            <GmList
              glvTokensApyData={glvApyInfoData}
              glvTokensIncentiveAprData={glvTokensIncentiveAprData}
              marketsTokensApyData={marketsTokensApyData}
              marketsTokensIncentiveAprData={marketsTokensIncentiveAprData}
              marketsTokensLidoAprData={marketsTokensLidoAprData}
              glvPerformance={undefined}
              gmPerformance={undefined}
              glvPerformanceSnapshots={undefined}
              gmPerformanceSnapshots={undefined}
              timeRange={timeRange}
              setTimeRange={setTimeRange}
              isDeposit
            />
          </div>
        )}

        {activeTab === PoolsTab.History && <TradeHistory account={account as Address} />}
      </div>

      <Footer />
    </div>
  );
}

function PoolsTvl() {
  const { chainId } = useChainId();
  const v2Stats = useV2Stats(chainId);

  const tvl = v2Stats?.totalGMLiquidity;

  return (
    <div className="mt-20 flex w-full flex-col gap-12 rounded-12 bg-gray-darker px-20 py-20 sm:mt-0 sm:w-auto sm:px-24 sm:py-24">
      <span className="text-body-medium font-medium text-gray-lighter">Total Value Locked in pool</span>
      <div className="flex flex-wrap items-center gap-12">
        <span className="text-white-font-medium text-h2 sm:text-h1">{formatUsd(tvl, { displayDecimals: 0 })}</span>
        {/* <Button
          variant="secondary"
          size="small"
          className="!px-18 !py-8"
          onClick={() => {
            document.getElementById("pools-list")?.scrollIntoView({ behavior: "smooth", block: "start" });
          }}
        >
          <Trans>Add Liquidity</Trans>
        </Button> */}
      </div>
    </div>
  );
}
